import React, { useState, useEffect } from "react";
import Icon from "@components/AppIcon";
import Image from "@components/AppImage";

const TrustSignals = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await fetch("/api/pages/home");

        if (!response.ok) {
          throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        // Extract trust_signals section
        const trustSignalsSection = data.homepage_sections?.find(
          (section) => section.type === "trust_signals" && section.enabled !== false
        );

        if (!trustSignalsSection) {
          setPartners([]);
          setLoading(false);
          return;
        }

        // Filter and transform only enabled partners
        const enabledPartners =
          trustSignalsSection.trust_signals?.[0]?.partners
            ?.filter((partner) => partner.enabled !== false)
            .map((partner) => ({
              id: partner.id,
              name: partner.partner_name || "",
              type: partner.partner_type || "",
              description: partner.partner_description || "",
              logo: partner.logo ? `/storage/${partner.logo}` : null,
            })) || [];

        setPartners(enabledPartners);
      } catch (err) {
        console.error("API Error:", err);
        setError(err.message);
        setPartners([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, []);

  const statistics = [
    {
      id: 1,
      number: "85,000+",
      label: "Patients Treated",
      description: "Successful treatments across all specialties",
      icon: "Users",
      color: "#08005f",
    },
    {
      id: 2,
      number: "98%",
      label: "Success Rate",
      description: "Across all surgical procedures",
      icon: "TrendingUp",
      color: "#eb2227",
    },
    {
      id: 3,
      number: "4.8/5",
      label: "Patient Rating",
      description: "Based on 10,000+ reviews",
      icon: "Star",
      color: "#f59e0b",
    },
    {
      id: 4,
      number: "25+",
      label: "Years Experience",
      description: "Serving Kenya's eye care needs",
      icon: "Calendar",
      color: "#08005f",
    },
  ];

  return (
    <section className="py-16 bg-gradient-to-br from-[#f5f6fa] via-[#e8eafd] to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2
            className="text-3xl sm:text-4xl font-heading font-bold mb-4"
            style={{ color: "#08005f" }}
          >
            Trusted by Thousands, Certified by Experts
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Our commitment to excellence is recognized by leading medical
            organizations, insurance providers, and most importantly, our
            patients.
          </p>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {statistics.map((stat) => (
            <div
              key={stat.id}
              className="bg-white rounded-xl p-6 text-center medical-shadow hover-lift medical-transition border border-[#08005f] border-opacity-10"
            >
              <div
                className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4"
                style={{ backgroundColor: "#08005f1a" }}
              >
                <Icon name={stat.icon} size={24} color={stat.color} />
              </div>
              <div
                className="text-3xl font-heading font-bold mb-2"
                style={{ color: "#08005f" }}
              >
                {stat.number}
              </div>
              <div
                className="font-semibold mb-1"
                style={{ color: "#eb2227" }}
              >
                {stat.label}
              </div>
              <div className="text-sm text-text-secondary">
                {stat.description}
              </div>
            </div>
          ))}
        </div>

        {/* Partners Section (with infinite scrolling) */}
        {partners.length > 0 && (
          <div className="mb-16">
            <h3 className="text-2xl font-heading font-bold text-[#08005f] text-center mb-8">
              Partners Who Support Us
            </h3>
            <div className="overflow-x-hidden relative">
              <div className="flex">
                <div
                  className="flex gap-8 animate-partner-slide"
                  style={{ animationDuration: `${10 + partners.length * 2}s` }}
                >
                  {partners.map((partner) => (
                    <PartnerCard key={partner.id} partner={partner} />
                  ))}
                  {/* Duplicate for seamless looping */}
                  {partners.map((partner) => (
                    <PartnerCard key={`${partner.id}-duplicate`} partner={partner} />
                  ))}
                </div>
              </div>

              {/* Animation styles */}
              <style>
                {`
                  @keyframes partner-slide {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(calc(-100% - 2rem)); }
                  }
                  .animate-partner-slide {
                    animation: partner-slide linear infinite;
                  }
                  .animate-partner-slide:hover {
                    animation-play-state: paused;
                  }
                  .line-clamp-1 {
                    overflow: hidden;
                    display: -webkit-box;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: 1;
                  }
                  .line-clamp-2 {
                    overflow: hidden;
                    display: -webkit-box;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: 2;
                  }
                `}
              </style>
            </div>
          </div>
        )}

        {/* Trust Guarantee */}
        <div className="bg-gradient-to-r from-[#08005f] to-[#08005f] rounded-2xl p-8 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Shield" size={32} color="white" />
            </div>
            <h3 className="text-2xl font-heading font-bold mb-4">
              Our Promise to You
            </h3>
            <p className="text-lg mb-6 opacity-90">
              We guarantee the highest standards of medical care, patient
              safety, and ethical practice. Your vision and trust are our
              most precious responsibilities.
            </p>
            <div className="grid md:grid-cols-3 gap-6 text-sm">
              <GuaranteeItem icon="CheckCircle" text="100% Ethical Practice" />
              <GuaranteeItem icon="Lock" text="Complete Privacy Protection" />
              <GuaranteeItem icon="Heart" text="Compassionate Care Always" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/** Partner Card Component */
const PartnerCard = ({ partner }) => (
  <div className="bg-white rounded-xl p-6 medical-shadow hover-lift medical-transition border border-[#08005f] border-opacity-10 min-w-[280px] h-[180px] flex-shrink-0 flex items-center justify-center">
    <div className="text-center w-full">
      {partner.logo ? (
        <div className="w-full h-20 mb-4 flex items-center justify-center">
          <Image
            src={partner.logo}
            alt={partner.name}
            className="max-w-full max-h-full object-contain mx-auto"
            width={120}
            height={80}
          />
        </div>
      ) : (
        <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center mx-auto mb-4">
          <Icon name="Building" size={24} color="#08005f" />
        </div>
      )}
      <h4 className="font-heading font-semibold text-[#08005f] text-sm mb-1 line-clamp-1">
        {partner.name}
      </h4>
      <div className="text-xs text-[#eb2227] font-semibold mb-2 line-clamp-1">
        {partner.type}
      </div>
      <p className="text-xs text-text-secondary line-clamp-2">
        {partner.description}
      </p>
    </div>
  </div>
);

/** Guarantee Item Component */
const GuaranteeItem = ({ icon, text }) => (
  <div className="flex items-center justify-center space-x-2">
    <Icon name={icon} size={20} />
    <span>{text}</span>
  </div>
);

export default TrustSignals;
